import { mergeDeepRight } from './mergeDeepRight';

type Translation = Record<PropertyKey, any>;

export type Translations<T = Record<string, unknown>> = T & {
  _translations?: Record<string, Translation>;
};

/**
 * Returns the most specific translation tag for the given locale.
 * @param data - translatable data
 * @param locale
 *
 * @returns The most specific translation tag, or null if no translation is available.
 */
export function getTranslationTag<T extends Record<string, any>>(data: Translations<T>, locale?: string): string | null {
  if (!data?.['_translations']) {
    return null;
  }

  if (locale != null) {
    /**
     * For a BCP 47 tag such as `nan-Hant-TW`, we want to search for translations in the most specific to least specific order:
     *
     * 1. nan-Hant-TW
     * 2. nan-Hant
     * 3. nan
     */
    const subtags = locale.split('-');

    for (let i = subtags.length; i > 0; i--) {
      const tag = subtags.slice(0, i).join('-');
      if (data['_translations'][tag]) {
        return tag;
      }
    }
  }
  return null;
}

export function translate<T extends Record<string, any>>(data: Translations<T>, locale?: string): T {
  const translationTag = getTranslationTag(data, locale);
  if (translationTag && data['_translations']) {
    return mergeDeepRight(data, data['_translations'][translationTag]);
  }

  return data;
}
